import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { cabinetApi, type PetBody, type PetView, type PropertyState } from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { PassportIcon, PlusIcon } from '../../shared/icons';
import { versionLabel } from '../../shared/lib/docNumber';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  CardList,
  EmptyState,
  ErrorState,
  ImageMark,
  LoadingState,
  Muted,
  NoticeBlock,
  PropertyCard,
  Screen,
  Section,
  toast,
} from '../../shared/ui';
import {
  describeProperty,
  PropertyActions,
  PropertyModeSwitch,
  PropertySearch,
  ReviewNote,
} from '../property/propertyKit';
import { ShowMore } from '../review/staffKit';
import { EMPTY_PET, PetForm, type PetFormValues } from './PetForm';

/**
 * Питомцы.
 *
 * Устроены как предметы склада: правка одобренного питомца не подменяет то,
 * что читают в карточке персонажа, а заводит новую версию и идёт через
 * проверку. Раньше питомцы публиковались сразу — экран обязан объяснить, что
 * теперь это не так, иначе игрок решит, что правка пропала.
 */

const EMPTY_TITLES: Record<PropertyState, string> = {
  published: t('pet.empty.published.title'),
  requests: t('common.empty.noRequests'),
  archived: t('common.empty.emptyArchive'),
};

const EMPTY_TEXTS: Record<PropertyState, string> = {
  published: t('pet.empty.published.text'),
  requests: t('pet.empty.requests.text'),
  archived: t('pet.empty.archived.text'),
};

type Editing = { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; petId: number };

export function PetsPanel({ id }: NavIdProps) {
  const back = useBack();
  const [state_, setState_] = useState<PropertyState>('published');
  const [editing, setEditing] = useState<Editing>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<number | null>(null);

  const search = useSearchTerm();

  const state = usePagedLoader<PetView>(
    (params, signal) =>
      cabinetApi.pets({ state: state_, search: search.term || undefined }, params, signal),
    [state_, search.term],
  );
  const pets = state.data?.items ?? [];

  const run = async (action: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await action();
      if (done !== undefined) toast(done);
      setEditing({ kind: 'none' });
      setConfirmArchive(null);
      state.reload();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const editingPet =
    editing.kind === 'edit' ? (pets.find((pet) => pet.id === editing.petId) ?? null) : null;

  return (
    <Screen
      id={id}
      title={t('common.section.pets')}
      back={back}
      count={state.data?.total}
      unit="pet"
    >
      {state.loading && state.data === null ? <LoadingState /> : null}

      {state.error !== null && state.data === null ? (
        <ErrorState title={t('pet.error.load')} message={state.error} onRetry={state.reload} />
      ) : null}

      {editing.kind === 'create' ? (
        <Section>
          <ReviewNote>{t('pet.note.create')}</ReviewNote>
          <PetForm
            initial={EMPTY_PET}
            submitLabel={t('pet.action.submit')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(() => cabinetApi.createPet(toBody(values)), t('pet.toast.created'))
            }
          />
        </Section>
      ) : null}

      {editingPet !== null ? (
        <Section>
          <ReviewNote>{editNote(editingPet)}</ReviewNote>

          <PetForm
            initial={toValues(editingPet)}
            submitLabel={t('common.action.save')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(
                () => cabinetApi.updatePet(editingPet.id, toBody(values, toValues(editingPet))),
                t('common.saved'),
              )
            }
          />
        </Section>
      ) : null}

      {editing.kind === 'none' && state.data !== null ? (
        <>
          {/* Раздел и поиск с кнопкой — одним блоком над списком. */}
          <Section>
            <PropertyModeSwitch state={state_} onChange={setState_} disabled={busy} />

            <PropertySearch
              value={search.input}
              onChange={search.setInput}
              placeholder={t('pet.search.placeholder')}
              action={
                state_ === 'published' ? (
                  <Action
                    tone="action"
                    size="m"
                    before={<PlusIcon />}
                    onClick={() => setEditing({ kind: 'create' })}
                  >
                    {t('pet.action.create')}
                  </Action>
                ) : null
              }
            />
          </Section>

          {/* Не пришли смена фильтра или дочитывание: на экране ещё прежние
              строки, и молчать об этом нельзя. */}
          {state.error !== null ? (
            <NoticeBlock
              tone="error"
              title={t('pet.error.load')}
              action={
                <Action tone="quiet" size="s" onClick={state.reload}>
                  {t('common.action.retry')}
                </Action>
              }
            >
              {state.error}
            </NoticeBlock>
          ) : null}

          <Section>
            {pets.length === 0 && state.error === null ? (
              <EmptyState title={EMPTY_TITLES[state_]}>{EMPTY_TEXTS[state_]}</EmptyState>
            ) : null}

            <CardList>
              {pets.map((pet) => (
                <PetCard
                  key={pet.id}
                  pet={pet}
                  busy={busy}
                  confirming={confirmArchive === pet.id}
                  onEdit={() => setEditing({ kind: 'edit', petId: pet.id })}
                  onAskArchive={() => setConfirmArchive(pet.id)}
                  onCancelArchive={() => setConfirmArchive(null)}
                  onArchive={(reason) =>
                    void run(() => cabinetApi.archivePet(pet.id, reason), t('pet.toast.archived'))
                  }
                />
              ))}
            </CardList>

            <ShowMore page={state.data} onMore={state.loadMore} loading={state.loading} />
          </Section>
        </>
      ) : null}
    </Screen>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Карточка питомца
   ────────────────────────────────────────────────────────────────────────── */

interface PetCardProps {
  pet: PetView;
  busy: boolean;
  confirming: boolean;
  onEdit: () => void;
  onAskArchive: () => void;
  onCancelArchive: () => void;
  onArchive: (reason: string) => void;
}

function PetCard({
  pet,
  busy,
  confirming,
  onEdit,
  onAskArchive,
  onCancelArchive,
  onArchive,
}: PetCardProps) {
  const status = describeProperty(pet);
  const archived = pet.deletedAt !== null;

  const photo = pet.imageId === null ? null : { id: pet.imageId };
  const passport = pet.passportImageId === null ? null : { id: pet.passportImageId };

  return (
    <PropertyCard
      image={photo}
      kind="pet"
      gallery={[photo, passport]}
      title={pet.name}
      badge={status.badge}
      tone={status.tone}
      // Номер версии показываем только со второй: у первой различать нечего.
      // Паспорт — отметка в том же ряду фактов, а не вторая миниатюра, но
      // отметка НАЖИМАЕМАЯ: у питомца без своего фото паспорт иначе некуда
      // открыть, а он и есть то, ради чего его смотрят.
      meta={[
        pet.species,
        pet.versionNo > 1 ? versionLabel(pet.versionNo) : null,
        passport === null ? null : (
          <ImageMark key="passport" image={passport}>
            <PassportIcon size={11} />
            {t('pet.card.passport')}
          </ImageMark>
        ),
      ]}
      description={pet.description}
      hint={status.hint}
      actions={
        archived ? (
          <Muted>{t('pet.card.archived')}</Muted>
        ) : (
          <PropertyActions
            subjectAccusative={t('pet.card.subject')}
            busy={busy}
            confirming={confirming}
            onEdit={onEdit}
            onAskArchive={onAskArchive}
            onCancelArchive={onCancelArchive}
            onArchive={onArchive}
          />
        )
      }
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Версия ↔ форма
   ────────────────────────────────────────────────────────────────────────── */

function editNote(pet: PetView): string {
  if (pet.status === 'rejected') return t('pet.note.rejected');
  if (pet.status !== 'approved') return t('pet.note.unpublished');
  return t('pet.note.published');
}

function toValues(pet: PetView): PetFormValues {
  return {
    name: pet.name,
    species: pet.species ?? '',
    description: pet.description ?? '',
    imageId: pet.imageId,
    passportImageId: pet.passportImageId,
  };
}

function toBody(values: PetFormValues, initial?: PetFormValues): PetBody {
  const body: PetBody = {
    name: values.name,
    species: values.species,
    description: values.description,
  };

  if (initial === undefined || values.imageId !== initial.imageId) {
    body.imageId = values.imageId;
  }

  if (initial === undefined || values.passportImageId !== initial.passportImageId) {
    body.passportImageId = values.passportImageId;
  }

  return body;
}
